// utils/permissions.js

// Check if user has a specific permission
export const hasPermission = (user, resource, action) => {
  if (!user) return false;
  
  // Super admin has all permissions
  if (user.role === 'super_admin') {
    return true;
  }

  // Front admin has specific permissions
  if (user.role === 'front_admin') {
    if (user.useCustomPermissions && user.customPermissions && user.customPermissions[resource]) {
      const resourcePermissions = user.customPermissions[resource] || [];
      return resourcePermissions.includes(action);
    }
    const permissions = getDefaultRolePermissions('front_admin');
    const resourcePermissions = permissions[resource] || [];
    return resourcePermissions.includes(action);
  }

  // Admin role has all permissions by default (unless custom permissions are explicitly set)
  if (user.role === 'admin') {
    // If custom permissions are enabled and resource is explicitly defined in custom permissions
    if (user.useCustomPermissions && user.customPermissions && user.customPermissions[resource]) {
      // Use custom permissions for this resource
      const resourcePermissions = user.customPermissions[resource] || [];
      return resourcePermissions.includes(action);
    }
    // Admin has all permissions by default (for both clinic and non-clinic resources)
    return true;
  }

  // Get effective permissions for other roles (staff, user)
  let permissions = {};
  
  // Use custom permissions only if enabled AND has the specific resource defined
  if (user.useCustomPermissions && user.customPermissions && user.customPermissions[resource]) {
    permissions = user.customPermissions;
  } else {
    // Use default role permissions
    permissions = getDefaultRolePermissions(user.role);
  }

  const resourcePermissions = permissions[resource] || [];
  return resourcePermissions.includes(action);
};

// Check if user has any permission for a resource
export const hasAnyPermission = (user, resource) => {
  if (!user) return false;
  
  // Super admin has all permissions
  if (user.role === 'super_admin') {
    return true;
  }

  // Front admin - check specific permissions
  if (user.role === 'front_admin') {
    if (user.useCustomPermissions && user.customPermissions && user.customPermissions[resource]) {
      const resourcePermissions = user.customPermissions[resource] || [];
      return resourcePermissions.length > 0;
    }
    const permissions = getDefaultRolePermissions('front_admin');
    const resourcePermissions = permissions[resource] || [];
    return resourcePermissions.length > 0;
  }

  // Admin role has all permissions by default
  if (user.role === 'admin') {
    // If custom permissions are enabled and resource is explicitly defined
    if (user.useCustomPermissions && user.customPermissions && user.customPermissions[resource]) {
      const resourcePermissions = user.customPermissions[resource] || [];
      return resourcePermissions.length > 0;
    }
    // Admin has all permissions by default
    return true;
  }

  let permissions = {};
  
  // Use custom permissions only if enabled AND has some permissions defined
  if (user.useCustomPermissions && user.customPermissions && Object.keys(user.customPermissions).length > 0) {
    permissions = user.customPermissions;
  } else {
    permissions = getDefaultRolePermissions(user.role);
  }

  const resourcePermissions = permissions[resource] || [];
  return resourcePermissions.length > 0;
};

// Get default permissions for a role (client-side fallback)
export const getDefaultRolePermissions = (role) => {
  const defaults = {
    user: {
      dashboard: ['read'],
      events: ['read'],
    },
    staff: {
      dashboard: ['read'],
      events: ['read', 'create', 'update'],
      jathas: ['read'],
      program_types: ['read'],
      sikh_holidays: ['read'],
      mainboard: ['read', 'create', 'update'],
      scrollboard: ['read', 'create', 'update'],
      notices: ['read', 'create', 'update'],
      // Staff can view finances but not modify
      expenses: ['read'],
      golak: ['read'],
      donations: ['read'],
      // Clinic permissions for staff
      'clinic.appointments': ['read', 'create', 'update'],
      'clinic.patients': ['read', 'create', 'update'],
      'clinic.staff': ['read'],
      'clinic.locations': ['read'],
      'clinic.services': ['read'],
      'clinic.rooms': ['read'],
      'clinic.forms': ['read', 'create'],
      'clinic.form_templates': ['read'],
      'clinic.form_packages': ['read'],
      'clinic.form_assignments': ['read', 'create', 'update'],
      'clinic.soap': ['read', 'create', 'update'],
      'clinic.soap_notes': ['read', 'create', 'update'],
    },
    front_admin: {
      dashboard: ['read'],
      'clinic.patients': ['read', 'create', 'update'],
      'clinic.appointments': ['read', 'create', 'update', 'status'],
      'clinic.staff': ['read'],
      'clinic.locations': ['read'],
      'clinic.services': ['read'],
      'clinic.rooms': ['read'],
      'clinic.forms': ['read', 'create'],
      'clinic.form_templates': ['read'],
      'clinic.form_packages': ['read'],
      'clinic.form_assignments': ['read', 'create', 'update', 'assign', 'review'],
      'clinic.soap': ['read'],
      'clinic.soap_notes': ['read'],
      'clinic.assessments': ['read'],
      'clinic.billing': ['read', 'create'],
    },
    admin: {
      dashboard: ['read', 'create', 'update', 'delete'],
      events: ['read', 'create', 'update', 'delete'],
      jathas: ['read', 'create', 'update', 'delete'],
      program_types: ['read', 'create', 'update', 'delete'],
      sikh_holidays: ['read', 'create', 'update', 'delete'],
      mainboard: ['read', 'create', 'update', 'delete'],
      scrollboard: ['read', 'create', 'update', 'delete'],
      notices: ['read', 'create', 'update', 'delete'],
      users: ['read', 'create', 'update', 'delete'],
      documents: ['read', 'create', 'update', 'delete'],
      // Admin has full finance access
      expenses: ['read', 'create', 'update', 'delete'],
      golak: ['read', 'create', 'update', 'delete'],
      donations: ['read', 'create', 'update', 'delete'],
      // Clinic permissions for admin - full access to all clinic resources
      'clinic.locations': ['read', 'create', 'update', 'delete'],
      'clinic.rooms': ['read', 'create', 'update', 'delete'],
      'clinic.services': ['read', 'create', 'update', 'delete'],
      'clinic.staff': ['read', 'create', 'update', 'delete'],
      'clinic.patients': ['read', 'create', 'update', 'delete'],
      'clinic.appointments': ['read', 'create', 'update', 'delete'],
      'clinic.forms': ['read', 'create', 'update', 'delete'],
      'clinic.form_templates': ['read', 'create', 'update', 'delete'],
      'clinic.form_packages': ['read', 'create', 'update', 'delete'],
      'clinic.form_assignments': ['read', 'create', 'update', 'delete'],
      'clinic.soap': ['read', 'create', 'update', 'delete'],
      'clinic.soap_templates': ['read', 'create', 'update', 'delete'],
      'clinic.soap_notes': ['read', 'create', 'update', 'delete'],
      'clinic.billing': ['read', 'create', 'update', 'delete'],
      'clinic.reports': ['read'],
      'clinic.audit_logs': ['read'],
      'clinic.settings': ['read', 'create', 'update', 'delete'],
    },
    super_admin: {
      // Super admin has all permissions (handled separately)
    },
  };

  // Return default for role, or basic dashboard access as fallback
  return defaults[role] || { dashboard: ['read'] };
};

// Check if user is admin (admin, front_admin, or super_admin)
export const isAdminUser = (user) => {
  return user && (user.role === 'admin' || user.role === 'super_admin' || user.role === 'front_admin');
};

// Filter navigation items based on user permissions
export const filterNavigationByPermissions = (navItems, user) => {
  if (!user) {
    console.log('[Permissions] No user, returning empty nav');
    return [];
  }
  
  // Super admin sees everything
  if (user.role === 'super_admin') {
    return navItems;
  }

  // Debug: log user permissions
  console.log('[Permissions] User:', user.name, 'Role:', user.role);
  console.log('[Permissions] useCustomPermissions:', user.useCustomPermissions);
  console.log('[Permissions] customPermissions:', user.customPermissions);

  return navItems
    .map(group => {
      // Filter submenu items
      const filteredSubmenu = group.submenu.filter(item => {
        // Check adminOnly flag - only admin/super_admin can see these
        if (item.adminOnly && !isAdminUser(user)) {
          console.log(`[Permissions] ${item.key} is adminOnly, user is ${user.role} - hiding`);
          return false;
        }
        
        // If no permission required, show item
        if (!item.permission) return true;
        
        const { resource, action } = item.permission;
        const hasPerm = hasPermission(user, resource, action);
        console.log(`[Permissions] Check ${resource}.${action}: ${hasPerm}`);
        return hasPerm;
      });

      // Only include group if it has visible items
      if (filteredSubmenu.length === 0) return null;

      return {
        ...group,
        submenu: filteredSubmenu,
      };
    })
    .filter(Boolean); // Remove null groups
};

// Get user's effective permissions (for display)
export const getUserPermissions = (user) => {
  if (!user) return {};
  
  if (user.role === 'super_admin') {
    return { _isSuperAdmin: true };
  }

  if (user.useCustomPermissions && user.customPermissions) {
    return user.customPermissions;
  }

  return getDefaultRolePermissions(user.role);
};
